import { useState, useEffect, useCallback } from 'react';
import { IdealCustomerProfile } from '../../types';
import { apiClient } from '../../lib/api';

export interface UseSignalWizardReturn {
  icpProfiles: IdealCustomerProfile[];
  signalSearchQuery: string | undefined;
  signalTableType: 'companies' | 'people' | 'custom' | undefined;
  signalPreviewData: any | undefined;
  handleSignalSelect: (query: string, type: 'companies' | 'people', icpId?: string, previewData?: any) => void;
  handleMultiSignalSelect: (signals: Array<{ query: string; type: 'companies' | 'people' }>) => void;
  handleSignalQueryConsumed: () => void;
  handleICPCreated: (icp: IdealCustomerProfile) => void;
}

interface UseSignalWizardProps {
  tableId: string | undefined;
  onNavigateAway?: () => void;
  closeWizard?: () => void;
}

export const useSignalWizard = ({ tableId, onNavigateAway, closeWizard }: UseSignalWizardProps): UseSignalWizardReturn => {
  const [icpProfiles, setIcpProfiles] = useState<IdealCustomerProfile[]>([]);
  const [signalSearchQuery, setSignalSearchQuery] = useState<string | undefined>();
  const [signalTableType, setSignalTableType] = useState<'companies' | 'people' | 'custom' | undefined>();
  const [signalPreviewData, setSignalPreviewData] = useState<any | undefined>();

  // Fetch ICP profiles for signal generator wizard
  useEffect(() => {
    apiClient.getICPProfiles()
      .then(setIcpProfiles)
      .catch((error) => console.error('Failed to fetch ICP profiles:', error));
  }, []);

  const handleSignalSelect = useCallback((query: string, type: 'companies' | 'people', _icpId?: string, previewData?: any) => {
    closeWizard?.();

    // Set all state atomically — query, type, and preview data arrive together
    setSignalSearchQuery(query);
    setSignalTableType(type);
    setSignalPreviewData(previewData);

    // Navigate to home screen if we're on a dataset view
    if (tableId) {
      onNavigateAway?.();
    }
  }, [closeWizard, tableId, onNavigateAway]);

  const handleSignalQueryConsumed = useCallback(() => {
    setSignalSearchQuery(undefined);
    setSignalTableType(undefined);
    setSignalPreviewData(undefined);
  }, []);

  const handleMultiSignalSelect = useCallback((signals: Array<{ query: string; type: 'companies' | 'people' }>) => {
    if (signals.length > 0) {
      handleSignalSelect(signals[0].query, signals[0].type);
    }
  }, [handleSignalSelect]);

  const handleICPCreated = useCallback((icp: IdealCustomerProfile) => {
    setIcpProfiles(prev => [...prev, icp]);
  }, []);

  return {
    icpProfiles,
    signalSearchQuery,
    signalTableType,
    signalPreviewData,
    handleSignalSelect,
    handleMultiSignalSelect,
    handleSignalQueryConsumed,
    handleICPCreated,
  };
};
